import React, { useState, Fragment } from 'react';
import { AnimatePresence } from 'framer-motion';
import { SlideTransition } from '../components/SlideTransition';
import { IntroSlide } from '../components/IntroSlide';
import { QuizSlide } from '../components/QuizSlide';
import { RewardSlide } from '../components/RewardSlide';
import { SummarySlide } from '../components/SummarySlide';
import { CTASlide } from '../components/CTASlide';
import { StoryboardView } from '../components/StoryboardView';
// Visualizations
import { DrugDevelopmentFunnel } from '../components/DrugDevelopmentFunnel';
import { TumourVisibilityComparison } from '../components/TumourVisibilityComparison';
import { ImagingEvolutionTimeline } from '../components/ImagingEvolutionTimeline';
import { FragmentedVsIntegrated } from '../components/FragmentedVsIntegrated';
// --- Data Definitions ---
type SlideType = 'intro' | 'quiz' | 'reward' | 'summary' | 'cta';
interface BaseSlide {
  id: string;
  type: SlideType;
}
interface IntroSlideData extends BaseSlide {
  type: 'intro';
}
interface QuizSlideData extends BaseSlide {
  type: 'quiz';
  questionNum: number;
  totalQuestions: number;
  headline: string;
  options: string[];
}
interface RewardSlideData extends BaseSlide {
  type: 'reward';
  topText?: string;
  pollPercentage: number;
  pollText: string;
  rolePolls?: {
    id: string;
    shortName: string;
    percentage: number;
    text: string;
  }[];
  insight: string[];
  pros?: string[];
  cons?: string[];
  expandableTitle?: string;
  expandableContent?: React.ReactNode;
  expandableBgImage?: string;
  visualization?: React.ReactNode;
}
interface SummarySlideData extends BaseSlide {
  type: 'summary';
}
interface CTASlideData extends BaseSlide {
  type: 'cta';
  headline: string;
  primaryButtonText: string;
  secondaryButtonText: string;
}
type SlideData = IntroSlideData | QuizSlideData | RewardSlideData | SummarySlideData | CTASlideData;
const slides: SlideData[] = [
// Slide 0 — Introduction
{
  id: 'intro',
  type: 'intro'
},
// Slide 1 — Role Selection
{
  id: 'role',
  type: 'quiz',
  questionNum: 1,
  totalQuestions: 5,
  headline: 'What best describes your role in oncology research or drug development?',
  options: ['Strategic leadership (portfolio / R&D strategy)', 'Program leadership (preclinical / clinical development)', 'Technical execution (biology / imaging / experiments)', 'Partner selection / outsourcing']
},
// Slide 2 — Role Result
{
  id: 'r_role',
  type: 'reward',
  topText: 'Your peer group',
  pollPercentage: 32,
  pollText: '32% of respondents share your role.',
  rolePolls: [{
    id: 'strategic',
    shortName: 'Strategic',
    percentage: 32,
    text: 'Strategic leadership — 32% of respondents'
  }, {
    id: 'program',
    shortName: 'Program',
    percentage: 29,
    text: 'Program leadership — 29% of respondents'
  }, {
    id: 'technical',
    shortName: 'Technical',
    percentage: 24,
    text: 'Technical execution — 24% of respondents'
  }, {
    id: 'partner',
    shortName: 'Partner',
    percentage: 15,
    text: 'Partner selection — 15% of respondents'
  }],
  insight: ['Different roles experience oncology development challenges from different perspectives.', 'Strategic leaders focus on pipeline risk and timelines. Program leaders focus on translational confidence. Technical teams focus on biological insight and experimental design.', 'Yet all ultimately depend on the same question:', 'Does the therapy actually work in the tumour?']
},
// Slide 3 — Problem Awareness
{
  id: 'q1',
  type: 'quiz',
  questionNum: 2,
  totalQuestions: 5,
  headline: 'Where does the greatest uncertainty typically occur in oncology drug development?',
  options: ['Manufacturing scalability', 'Understanding whether the therapy actually reaches and affects tumours', 'Regulatory approval pathways']
},
// Slide 4 — Problem Awareness Reward
{
  id: 'r1',
  type: 'reward',
  topText: 'Insight',
  pollPercentage: 52,
  pollText: '52% of respondents answered like you.',
  rolePolls: [{
    id: 'strategic',
    shortName: 'Strategic',
    percentage: 48,
    text: '48% of Strategic leaders answered like you.'
  }, {
    id: 'program',
    shortName: 'Program',
    percentage: 58,
    text: '58% of Program leaders answered like you.'
  }, {
    id: 'technical',
    shortName: 'Technical',
    percentage: 62,
    text: '62% of Technical teams answered like you.'
  }, {
    id: 'partner',
    shortName: 'Partner',
    percentage: 40,
    text: '40% of Partner selectors answered like you.'
  }],
  visualization: <DrugDevelopmentFunnel data-id="element-310" />,
  insight: ['Manufacturing and regulatory pathways are important.', 'But many oncology programs fail earlier — when researchers cannot clearly determine whether a therapy reaches its target or alters tumour biology.', 'Understanding drug behaviour inside tumours is therefore a critical step in reducing development risk.']
},
// Slide 5 — Mechanism Awareness
{
  id: 'q2',
  type: 'quiz',
  questionNum: 3,
  totalQuestions: 5,
  headline: 'Which approach most improves early confidence in oncology drug development?',
  options: ['Larger preclinical datasets', 'Real-time biological insight using molecular imaging', 'Earlier clinical trials']
},
// Slide 6 — Mechanism Awareness Reward
{
  id: 'r2',
  type: 'reward',
  topText: 'Insight',
  pollPercentage: 48,
  pollText: '48% of respondents answered like you.',
  rolePolls: [{
    id: 'strategic',
    shortName: 'Strategic',
    percentage: 42,
    text: '42% of Strategic leaders answered like you.'
  }, {
    id: 'program',
    shortName: 'Program',
    percentage: 55,
    text: '55% of Program leaders answered like you.'
  }, {
    id: 'technical',
    shortName: 'Technical',
    percentage: 68,
    text: '68% of Technical teams answered like you.'
  }, {
    id: 'partner',
    shortName: 'Partner',
    percentage: 38,
    text: '38% of Partner selectors answered like you.'
  }],
  visualization: <TumourVisibilityComparison data-id="element-311" />,
  insight: ['More data can strengthen models.', 'But understanding what happens inside the tumour in real time provides a different type of confidence.', 'Molecular imaging allows researchers to observe drug distribution, target binding, and tumour response in vivo, improving early biological insight.']
},
// Slide 7 — Industry Shift
{
  id: 'q3',
  type: 'quiz',
  questionNum: 4,
  totalQuestions: 5,
  headline: 'How are imaging priorities evolving in oncology research today?',
  options: ['Imaging remains primarily diagnostic', 'Imaging is becoming central to translational drug development', 'Imaging mainly supports regulatory submissions']
},
// Slide 8 — Industry Shift Reward
{
  id: 'r3',
  type: 'reward',
  topText: 'Insight',
  pollPercentage: 57,
  pollText: '57% of respondents answered like you.',
  rolePolls: [{
    id: 'strategic',
    shortName: 'Strategic',
    percentage: 65,
    text: '65% of Strategic leaders answered like you.'
  }, {
    id: 'program',
    shortName: 'Program',
    percentage: 60,
    text: '60% of Program leaders answered like you.'
  }, {
    id: 'technical',
    shortName: 'Technical',
    percentage: 52,
    text: '52% of Technical teams answered like you.'
  }, {
    id: 'partner',
    shortName: 'Partner',
    percentage: 48,
    text: '48% of Partner selectors answered like you.'
  }],
  visualization: <ImagingEvolutionTimeline data-id="element-312" />,
  insight: ['Historically, imaging helped diagnose disease and monitor treatment response.', 'Today it is increasingly used earlier — to understand drug behaviour, biological targeting, and tumour interaction during development.', 'This reflects a broader shift toward translational oncology platforms.']
},
// Slide 9 — Partner Selection
{
  id: 'q4',
  type: 'quiz',
  questionNum: 5,
  totalQuestions: 5,
  headline: 'When selecting partners for oncology drug development, what matters most today?',
  options: ['Best-in-class individual technologies', 'Integrated translational platforms', 'Lowest-cost service providers']
},
// Slide 10 — Partner Selection Reward
{
  id: 'r4',
  type: 'reward',
  topText: 'Insight',
  pollPercentage: 46,
  pollText: '46% of respondents answered like you.',
  rolePolls: [{
    id: 'strategic',
    shortName: 'Strategic',
    percentage: 55,
    text: '55% of Strategic leaders answered like you.'
  }, {
    id: 'program',
    shortName: 'Program',
    percentage: 48,
    text: '48% of Program leaders answered like you.'
  }, {
    id: 'technical',
    shortName: 'Technical',
    percentage: 35,
    text: '35% of Technical teams answered like you.'
  }, {
    id: 'partner',
    shortName: 'Partner',
    percentage: 62,
    text: '62% of Partner selectors answered like you.'
  }],
  visualization: <FragmentedVsIntegrated data-id="element-313" />,
  insight: ['Modern oncology development involves multiple complex technologies.', 'Many organizations are therefore shifting from isolated tools toward integrated translational platforms that combine imaging, disease models, and translational expertise.', 'Integration can reduce coordination complexity and accelerate decision-making.']
},
// Slide 11 — Summary Slide
{
  id: 'summary',
  type: 'summary'
},
// Slide 12 — Final CTA
{
  id: 'cta',
  type: 'cta',
  headline: 'See how oncology research priorities are evolving.',
  primaryButtonText: 'Explore the insights',
  secondaryButtonText: 'Learn more about platforms'
}];
export function OncologyQuiz() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [showStoryboard, setShowStoryboard] = useState(false);
  const currentSlide = slides[currentIndex];
  const handleNext = () => {
    if (currentIndex < slides.length - 1) {
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  };
  const handleQuizSelect = (optionIndex: number) => {
    handleNext();
  };
  return <div className="w-full h-[100dvh] max-h-[100dvh] bg-slate-200 flex items-center justify-center overflow-hidden font-sans" data-id="element-314">
      {/* Mobile container constraint */}
      <div className="w-full h-full max-w-md bg-[#0B1623] relative overflow-hidden shadow-2xl" data-id="element-315">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-316">
          <SlideTransition key={currentSlide.id} direction={direction} data-id="element-317">
            {currentSlide.type === 'intro' && <IntroSlide onStart={handleNext} onSkip={handleNext} data-id="element-318" />}

            {currentSlide.type === 'quiz' && <QuizSlide questionNum={currentSlide.questionNum} totalQuestions={currentSlide.totalQuestions} headline={currentSlide.headline} options={currentSlide.options} onSelect={handleQuizSelect} onBack={handleBack} onSkip={handleNext} data-id="element-319" />}

            {currentSlide.type === 'reward' && <RewardSlide topText={currentSlide.topText} pollPercentage={currentSlide.pollPercentage} pollText={currentSlide.pollText} rolePolls={currentSlide.rolePolls} insight={currentSlide.insight} pros={currentSlide.pros} cons={currentSlide.cons} expandableTitle={currentSlide.expandableTitle} expandableContent={currentSlide.expandableContent} expandableBgImage={currentSlide.expandableBgImage} visualization={currentSlide.visualization} onNext={handleNext} onBack={handleBack} onSkip={handleNext} data-id="element-320" />}

            {currentSlide.type === 'summary' && <SummarySlide onNext={handleNext} onBack={handleBack} onSkip={handleNext} data-id="element-321" />}

            {currentSlide.type === 'cta' && <CTASlide headline={currentSlide.headline} primaryButtonText={currentSlide.primaryButtonText} secondaryButtonText={currentSlide.secondaryButtonText} onBack={handleBack} data-id="element-322" />}
          </SlideTransition>
        </AnimatePresence>

        {/* Storyboard floating button */}
        <button onClick={() => setShowStoryboard(true)} className="absolute bottom-5 right-5 z-[50] w-10 h-10 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20 transition-all shadow-lg pointer-events-auto" aria-label="View storyboard" title="View storyboard" style={{
        zIndex: 50
      }} data-id="element-323">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0" data-id="element-324">
            <rect x="1" y="1" width="5" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" data-id="element-325" />
            <rect x="10" y="1" width="5" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" data-id="element-326" />
            <rect x="1" y="9" width="5" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" data-id="element-327" />
            <rect x="10" y="9" width="5" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" data-id="element-328" />
          </svg>
        </button>
      </div>

      {/* Storyboard overlay — outside mobile container */}
      <StoryboardView isOpen={showStoryboard} onClose={() => setShowStoryboard(false)} data-id="element-329" />
    </div>;
}